// src/features/clients/components/EditClientModal.jsx
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import toast from "react-hot-toast";

// ✅ Função auxiliar híbrida CPF/CNPJ
const formatCpfCnpj = (value) => {
  if (!value) return "";

  const numericValue = value.replace(/\D/g, ""); // Remove não-dígitos

  // Máscara de CPF (até 11 dígitos)
  if (numericValue.length <= 11) {
    return numericValue
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1");
  }

  // Máscara de CNPJ (acima de 11 dígitos)
  return numericValue
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .replace(/(-\d{2})\d+?$/, "$1");
};

function EditClientModal({ isOpen, onClose, onSave, clientData }) {
  const {
    register,
    handleSubmit,
    reset,
  } = useForm();

  useEffect(() => {
    if (clientData) {
      // ✅ Aplica a máscara correta ao carregar, dependendo do tamanho do dado salvo
      reset({
        name: clientData.name,
        phone: clientData.phone,
        cpf: formatCpfCnpj(clientData.cpf || ""),
        city: clientData.city || "",
      });
    }
  }, [clientData, reset]);

  const onSubmit = async (data) => {
    if (!data.name) {
      toast.error("Nome é obrigatório.");
      return;
    }

    // ✅ Limpa a formatação (remove pontos, traços e barras) antes de salvar
    const cleanDoc = data.cpf ? data.cpf.replace(/\D/g, "") : "";

    // Validação simples de tamanho ao salvar (opcional, mas recomendada)
    if (
      cleanDoc.length > 0 &&
      cleanDoc.length !== 11 &&
      cleanDoc.length !== 14
    ) {
      toast.error(
        "Documento inválido (deve ter 11 dígitos para CPF ou 14 para CNPJ)."
      );
      return;
    }

    await onSave({
      ...data,
      cpf: cleanDoc,
      phone: clientData.phone,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full h-[100dvh] max-h-none sm:h-auto sm:max-h-[85vh] sm:max-w-md p-0 border-0 sm:border rounded-none sm:rounded-lg overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-slate-100 dark:border-border shrink-0">
          <DialogTitle>Editar Cliente</DialogTitle>
        </DialogHeader>
        <div className="px-6 py-6 overflow-y-auto flex-1">
          <form id="edit-client-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="name">Nome Completo</Label>
                <Input
                  id="name"
                  {...register("name", { required: "Nome é obrigatório" })}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  {...register("phone")}
                  className="bg-gray-100 text-gray-500 cursor-not-allowed"
                  disabled
                  title="Para mudar o telefone, exclua e crie novamente."
                />
              </div>

            {/* ✅ 3. Campo Híbrido CPF/CNPJ */}
              <div className="space-y-1">
                <Label htmlFor="cpf">CPF/CNPJ</Label>
                <Input
                  id="cpf"
                  {...register("cpf", {
                    onChange: (e) => {
                      // Aplica a máscara dinâmica (muda de visual ao passar do 11º dígito)
                      e.target.value = formatCpfCnpj(e.target.value);
                    },
                  })}
                  placeholder="CPF ou CNPJ"
                  maxLength={18} // Aumentado para caber CNPJ formatado (14 números + 4 símbolos)
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="city">Cidade</Label>
                <Input id="city" {...register("city")} placeholder="Ex: Aracaju" />
              </div>
            </div>
          </form>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-slate-100 dark:border-border bg-slate-50 dark:bg-card shrink-0">
          <Button form="edit-client-form" type="submit" size="lg" className="w-full sm:w-auto min-h-[44px]">
            Salvar Alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditClientModal;
